import { z } from 'zod';

/**
 * Zod validation schema factory for Form K - Controlling Owners
 * Creates a schema with translated error messages
 */
export const createFormKSchema = (tForms: (key: string) => string) =>
  z
    .object({
      // 1. Origin of assets (multi-select)
      originOfAssets: z.array(z.string()).optional(),
      originOfAssetsOther: z.string().optional(),

      // 2. Controlling owner determination type (radio - required)
      controllingOwnerType: z
        .string()
        .min(1, tForms('formK.validation.controllingOwnerTypeRequired')),

      // 3. Controlling owners (repeatable group)
      controllingOwners: z
        .array(
          z.object({
            lastName: z
              .string()
              .min(1, tForms('formK.validation.lastNameRequired')),
            firstName: z
              .string()
              .min(1, tForms('formK.validation.firstNameRequired')),
            address: z
              .string()
              .min(1, tForms('formK.validation.addressRequired')),
            postalCode: z
              .string()
              .min(1, tForms('formK.validation.postalCodeRequired')),
            city: z.string().min(1, tForms('formK.validation.cityRequired')),
            country: z
              .string()
              .min(1, tForms('formK.validation.countryRequired'))
              .length(2, tForms('formK.validation.countryInvalidFormat')),
            companyName: z.string().optional(),
            nationality: z
              .string()
              .min(1, tForms('formK.validation.nationalityRequired'))
              .length(2, tForms('formK.validation.nationalityInvalidFormat')),
            birthDate: z.date().optional(),
          })
        )
        .min(1, tForms('formK.validation.controllingOwnersMinimum')),

      // 4. Shareholders other companies ≥25% (radio yes/no - required)
      shareholdersOtherCompanies: z
        .string()
        .min(1, tForms('formK.validation.shareholdersOtherCompaniesRequired')),

      // 5. Fiduciary holding of assets (radio yes/no - required)
      fiduciaryHolding: z
        .string()
        .min(1, tForms('formK.validation.fiduciaryHoldingRequired')),
    })
    .superRefine((data, ctx) => {
      // Conditional validation for Origin of Assets - Other text field
      // Required if "other" is selected in the multi-select originOfAssets
      const hasOtherOrigin = data.originOfAssets?.includes('other');
      if (hasOtherOrigin && !data.originOfAssetsOther?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['originOfAssetsOther'],
          message: tForms('formK.validation.originOfAssetsOtherRequired'),
        });
      }
    });

/**
 * Type inference for Form K data from the Zod schema
 */
export type FormKData = z.infer<ReturnType<typeof createFormKSchema>>;

/**
 * Individual controlling owner entry structure
 */
export interface ControllingOwner {
  lastName: string;
  firstName: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  companyName?: string;
  nationality: string;
  birthDate?: Date;
}

/**
 * Props for the main FormK component
 */
export interface FormKProps {
  onSubmit?: (data: FormKData) => void;
  onError?: (error: Error) => void;
  onChange?: (data: Partial<FormKData>) => void;
  className?: string;
  defaultValues?: Partial<FormKData>;
}

/**
 * Ref handle for FormK component exposing validation method
 */
export interface FormKHandle {
  triggerValidation: () => Promise<boolean>;
}
